/**
 * IconButton — bouton compact contenant uniquement une icône.
 *
 * @remarks
 * `aria-label` est **obligatoire** (TypeScript-enforced) puisqu'il n'y a pas
 * de texte visible — sinon les screen readers annoncent un bouton anonyme.
 *
 * `loading` remplace l'icône par le `Spinner` et désactive le bouton — même
 * grammaire que `Button`, pour les actions icône seule (édition, suppression
 * en ligne dans une liste) qui déclenchent une mutation.
 */

import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Spinner } from './spinner';
import { cn } from '@/lib/utils/cn';

type IconButtonSize = 'sm' | 'md' | 'lg';

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Obligatoire pour l'accessibilité. */
  'aria-label': string;
  size?: IconButtonSize;
  active?: boolean;
  loading?: boolean;
}

/** L'icône prend une taille de l'échelle selon le bouton (SAAS-1184), sauf classe `size-*` explicite. */
const SIZES: Record<IconButtonSize, string> = {
  sm: 'size-7 [&_svg:not([class*=size-])]:size-(--icon-size-sm)',
  md: 'size-9 [&_svg:not([class*=size-])]:size-(--icon-size-md)',
  lg: 'size-10 [&_svg:not([class*=size-])]:size-(--icon-size-md)',
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    size = 'md',
    active = false,
    loading = false,
    className,
    type = 'button',
    disabled,
    children,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={loading || (disabled ?? false)}
      className={cn(
        'inline-flex items-center justify-center rounded-md transition-colors',
        'text-fg-muted hover:bg-bg-muted hover:text-fg',
        'focus-visible:outline-primary focus-visible:outline-2 focus-visible:outline-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        active && 'bg-bg-muted text-fg',
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size="sm" /> : children}
    </button>
  );
});
